/**
 * Pure scheduling-conflict detection for Game Pass monthly challenges
 * (campaigns) — FE-2331.
 *
 * An admin must not be able to schedule a monthly challenge whose date range
 * overlaps another campaign that already occupies a committed time window —
 * i.e. an ACTIVE or SCHEDULED campaign. Draft, completed and cancelled
 * campaigns never block a new schedule (a draft has no committed window yet).
 *
 * This is the campaign-scoped mirror of `seasonScheduleConflict.ts`
 * (FE-1693): same half-open `[start, end)` UTC interval model, so two
 * back-to-back challenges (one ends exactly when the next begins) do NOT
 * conflict. Kept framework-free (no React) so it can be unit-tested in
 * isolation and reused by every surface that schedules a campaign.
 *
 * Product note: unlike Seasons (one active at a time), the monthly listing can
 * render multiple concurrent active challenges. This guard still enforces
 * *no time-overlap* at schedule time on the frontend — the BE may or may not
 * enforce the same rule server-side (see FE-2331).
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AppStrings } from "../../utils/constants/AppStrings";
import {
  CAMPAIGN_API_STATUS,
  type CampaignApiRecord,
  type CampaignApiStatus,
} from "../../types/gamePass";
import type { MonthlyScheduleValues } from "../../components/GamePass/Monthly/monthlyScheduleData";
import { combineDateAndTime } from "./useUpdateSeason";

dayjs.extend(utc);

/** Lifecycle statuses whose date window blocks a new campaign's schedule from
 *  overlapping. Only ACTIVE and SCHEDULED campaigns hold a committed window —
 *  draft, completed and cancelled campaigns no longer block a schedule. */
export const CAMPAIGN_CONFLICT_STATUSES: ReadonlySet<CampaignApiStatus> =
  new Set([CAMPAIGN_API_STATUS.ACTIVE, CAMPAIGN_API_STATUS.SCHEDULED]);

/** A resolved UTC millisecond window for a campaign's [start, end). */
export type CampaignScheduleRange = {
  startMs: number;
  endMs: number;
};

/** A real monthly challenge runs for days/weeks. Any existing window shorter
 *  than this is treated as an unscheduled placeholder (the Create wizard stamps
 *  a ~1-minute placeholder window on new drafts because the BE requires
 *  end > start on create) and is skipped by the overlap guard so a half-finished
 *  draft can never falsely block another campaign. Mirrors the Seasons guard. */
const MIN_REAL_CAMPAIGN_WINDOW_MS = 60 * 60 * 1000; // 1 hour

const toUtcMs = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

/**
 * Resolve the candidate campaign's UTC window from the Schedule step values.
 * Returns `null` only when there is no fixed window to compare — i.e. a
 * date/time field is still missing (or the window is non-positive).
 *
 * Auto-start IS overlap-checked: toggling it on fills a concrete, editable
 * window, so the resulting range can still collide with another scheduled /
 * active campaign. The half-open `[start, end)` intersection means the
 * auto-start challenge never flags the very season it chains after (it begins
 * one minute later).
 */
export const resolveCandidateScheduleRange = (
  schedule: MonthlyScheduleValues,
): CampaignScheduleRange | null => {
  const startIso = combineDateAndTime(schedule.startDate, schedule.startTime);
  const endIso = combineDateAndTime(schedule.endDate, schedule.endTime);
  const startMs = toUtcMs(startIso);
  const endMs = toUtcMs(endIso);
  if (startMs == null || endMs == null) return null;
  // A non-positive window (end ≤ start) isn't a real schedule to compare —
  // treat it as "no window" rather than risk a degenerate overlap.
  if (startMs >= endMs) return null;
  return { startMs, endMs };
};

/** Half-open interval intersection: `[aStart, aEnd)` overlaps `[bStart, bEnd)`. */
const rangesOverlap = (
  a: CampaignScheduleRange,
  b: CampaignScheduleRange,
): boolean => a.startMs < b.endMs && b.startMs < a.endMs;

export type FindCampaignScheduleConflictArgs = {
  /** The Schedule step values the admin is trying to save. */
  schedule: MonthlyScheduleValues;
  /** Every campaign currently known to the client (any status — this fn filters). */
  existingCampaigns: ReadonlyArray<CampaignApiRecord>;
  /** Id of the campaign being scheduled/edited so it never conflicts with itself. */
  excludeCampaignId?: string | null;
};

/**
 * Returns the first existing campaign whose window overlaps the candidate
 * schedule, or `null` when there is no conflict (or no fixed window to check).
 * The candidate's own record is skipped via `excludeCampaignId` so editing a
 * campaign doesn't flag it against itself.
 */
export const findCampaignScheduleConflict = ({
  schedule,
  existingCampaigns,
  excludeCampaignId,
}: FindCampaignScheduleConflictArgs): CampaignApiRecord | null => {
  const candidate = resolveCandidateScheduleRange(schedule);
  if (!candidate) return null;

  for (const record of existingCampaigns) {
    const recordId = record.campaign_id ?? record.id ?? null;
    if (excludeCampaignId && recordId === excludeCampaignId) continue;
    if (!CAMPAIGN_CONFLICT_STATUSES.has(record.status)) continue;

    const startMs = toUtcMs(record.start_date);
    const endMs = toUtcMs(record.end_date);
    if (startMs == null || endMs == null) continue;
    // Skip campaigns with no real window. New drafts are stamped with a tiny
    // placeholder window (the BE requires end > start, so a zero-width one is
    // rejected) until the admin sets real dates on the Schedule step, so an
    // unscheduled draft never blocks another campaign.
    if (endMs - startMs < MIN_REAL_CAMPAIGN_WINDOW_MS) continue;

    if (rangesOverlap(candidate, { startMs, endMs })) {
      return record;
    }
  }
  return null;
};

/**
 * Build the user-facing error message for a detected conflict. Names the
 * conflicting campaign so the admin knows which one to move; falls back to a
 * generic message when the record has no name.
 */
export const buildCampaignScheduleConflictMessage = (
  conflict?: CampaignApiRecord | null,
): string => {
  const name = conflict?.name?.trim();
  if (!name) return AppStrings.MONTHLY_SCHEDULE_CONFLICT_GENERIC;
  return AppStrings.MONTHLY_SCHEDULE_CONFLICT_NAMED.replace(
    "{challengeName}",
    name,
  );
};
